import type { PropsWithChildren, ReactChild } from 'react';

import type { ButtonProps } from '@capsule/consumer-design-system';
import { ButtonWithLinkStyle, Typography } from '@capsule/consumer-design-system';

import { motion, useReducedMotion } from 'framer-motion';
import type { AnimationProps } from 'framer-motion';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import type { LayoutCTAGroupProps } from './LayoutCTAGroup';
import { LayoutCTAGroup } from './LayoutCTAGroup';

/** 300 milliseconds */
const INITIAL_ANIMATION_DELAY = 0.3;

/** 600 milliseconds */
const LAST_ANIMATION_DELAY = INITIAL_ANIMATION_DELAY * 2;

export interface LayoutProps extends PropsWithChildren<LayoutCTAGroupProps> {
  title: string;
  description?: ReactChild;
  onBack?: () => void;
}

const LayoutContainer = styled.div`
  --mobileGutterSizeOnboardingLayout: ${({ theme }) => theme.space.s5};

  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  height: 100%;

  ${({ theme }) => theme.mediaQueries.small} {
    gap: ${({ theme }) => theme.space.s7};
    position: relative;
    max-width: 600px;
    margin: 0 auto 0;
  }
`;

const StepMain = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space.s7} var(--mobileGutterSizeOnboardingLayout) 16rem;
  height: calc(100% + 8rem);
  overflow-y: auto;

  ${({ theme }) => theme.mediaQueries.small} {
    position: unset;
    height: auto;
    display: block;
    padding: 0;
    padding-top: ${({ theme }) => theme.space.s7};
    overflow-y: visible;
  }
`;

const StepHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.s6};

  margin-bottom: ${({ theme }) => theme.space.s7};

  ${({ theme }) => theme.mediaQueries.small} {
    gap: ${({ theme }) => theme.space.s5};
    margin-top: 0;
  }
`;

const StyledLinkStyleButton = styled(ButtonWithLinkStyle)`
  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.space.s1};
  }

  & svg {
    width: 18px;
    height: 18px;
  }
`;

const TitleAndDescriptionContainer = styled.header`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s5};

  & > * {
    margin: 0;
  }
`;

const StepBody = styled(Typography).attrs({ as: motion.div, variant: 'body' })`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.s5};

  & > * {
    width: 100%;
  }

  & > svg {
    margin: 0 auto;
    width: 200px;
  }

  ${({ theme }) => theme.mediaQueries.smallDown} {
    flex-grow: 1;
    & > svg {
      width: 100px;
    }

    /* This gives extra room to scroll */
    padding-bottom: ${({ theme }) => theme.space.s5};
  }
`;

const backBtnAnimProps: AnimationProps = {
  initial: { opacity: '0%' },
  animate: { opacity: '100%' },
  transition: { duration: 0.3, ease: 'easeIn', delay: LAST_ANIMATION_DELAY },
};

const getPrimaryAnimProps = ({
  delay,
  shouldReduceMotion,
}: {
  /** In seconds */
  delay: number;
  shouldReduceMotion: boolean;
}): AnimationProps => ({
  initial: { opacity: '0%', translateY: shouldReduceMotion ? '0rem' : '0.8rem' },
  animate: { opacity: '100%', translateY: '0rem' },
  transition: { duration: 0.3, ease: 'easeIn', delay },
});

const BackButton = ({ onClick }: Pick<ButtonProps, 'onClick'>) => {
  return (
    <motion.div {...backBtnAnimProps} key={`layout-back-button`}>
      <StyledLinkStyleButton onClick={onClick}>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            d="M14.7071 4.29289C15.0976 3.90237 15.7308 3.90237 16.1213 4.29289C16.5118 4.68342 16.5118 5.31658 16.1213 5.70711L9.82843 12L16.1213 18.2929C16.5118 18.6834 16.5118 19.3166 16.1213 19.7071C15.7308 20.0976 15.0976 20.0976 14.7071 19.7071L7 12L14.7071 4.29289Z"
            fill="currentColor"
          />
        </svg>
        Back
      </StyledLinkStyleButton>
    </motion.div>
  );
};

export const FailedOrderRescheduleLayout = ({
  className,
  onBack,
  title,
  description,
  children,
  cta1,
  cta2,
  error,
}: LayoutProps) => {
  const history = useHistory();
  const shouldReduceMotion = !!useReducedMotion();

  const back = () => {
    onBack?.();
    history.goBack();
  };

  const initialAnimationProps = getPrimaryAnimProps({ shouldReduceMotion, delay: INITIAL_ANIMATION_DELAY });
  const lastAnimationProps = getPrimaryAnimProps({ shouldReduceMotion, delay: LAST_ANIMATION_DELAY });

  return (
    <LayoutContainer className={className}>
      <StepMain>
        <StepHeader>
          <BackButton onClick={back} key={title} />

          <TitleAndDescriptionContainer>
            <Typography as={motion.h2} variant={{ base: 'heading2', small: 'displayXl' }} {...initialAnimationProps}>
              {title}
            </Typography>

            {description && (
              <Typography
                as={typeof description === 'string' ? motion.p : motion.div}
                variant="body"
                {...lastAnimationProps}
              >
                {description}
              </Typography>
            )}
          </TitleAndDescriptionContainer>
        </StepHeader>

        <StepBody {...lastAnimationProps}>{children}</StepBody>
      </StepMain>

      <LayoutCTAGroup cta1={cta1} cta2={cta2} error={error} {...lastAnimationProps} />
    </LayoutContainer>
  );
};
